import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { User, Lock, CreditCard, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { changePassword, cancelSubscription, resumeSubscription } from '../../api/client';
import { AppError } from '../../api/request';
import { panelPath } from '../../config/paths';

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function subStatusKey(status?: string): string {
  switch (status) {
    case 'active':
      return 'profile.subStatusActive';
    case 'trialing':
      return 'profile.subStatusTrialing';
    case 'expired':
      return 'profile.subStatusExpired';
    case 'cancelled':
      return 'profile.subStatusCancelled';
    default:
      return 'profile.noSubscription';
  }
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, refetchUser, updateUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeSuccess, setResumeSuccess] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);
    if (newPassword.length < 6) {
      setPasswordError(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordError(t('auth.passwordMismatch'));
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (err) {
      setPasswordError(err instanceof AppError ? err.userMessage : t('profile.passwordError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelError(null);
    setCancelLoading(true);
    try {
      await cancelSubscription();
      await refetchUser();
      setCancelConfirmOpen(false);
      setCancelSuccess(true);
      setCancelError(null);
    } catch (err) {
      setCancelError(err instanceof AppError ? err.userMessage : t('profile.cancelError'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setResumeError(null);
    setResumeSuccess(false);
    setResumeLoading(true);
    try {
      const updated = await resumeSubscription();
      updateUser(updated);
      setResumeSuccess(true);
    } catch (err) {
      setResumeError(err instanceof AppError ? err.userMessage : t('profile.reactivateError'));
    } finally {
      setResumeLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const isTrialing = user.subscriptionStatus === 'trialing';
  const hasProSubscription = user.hasProSubscription === true;
  const canCancelSubscription = user.canCancelSubscription === true;
  const showReactivate = user.canReactivateSubscription === true;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <User className="h-7 w-7 text-primary-500" />
        {t('profile.title')}
      </h1>

      <section className="mb-8">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('profile.email')} ({user.email})
        </label>
        <input
          type="email"
          value={user.email}
          readOnly
          disabled
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-2 cursor-not-allowed"
          title={t('profile.emailReadOnly')}
        />
      </section>

      <section className="mb-8">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('profile.memberSince')}
        </label>
        <p className="text-gray-900 dark:text-white">
          {formatDate(user.createdAt)}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Lock className="h-5 w-5 text-primary-500" />
          {t('profile.changePassword')}
        </h2>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('profile.currentPassword')}
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:ring-2 focus:ring-primary-500"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('profile.newPassword')}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:ring-2 focus:ring-primary-500"
              autoComplete="new-password"
              minLength={6}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('profile.newPasswordConfirm')}
            </label>
            <input
              type="password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:ring-2 focus:ring-primary-500"
              autoComplete="new-password"
            />
          </div>
          {passwordSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400">
              {t('profile.passwordUpdated')}
            </p>
          )}
          {passwordError && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {passwordError}
            </p>
          )}
          <button
            type="submit"
            disabled={passwordLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {passwordLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('profile.savePassword')}
          </button>
        </form>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary-500" />
          {t('profile.subscription')}
        </h2>
        <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t('profile.subscriptionStatus')}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {t(subStatusKey(user.subscriptionStatus))}
            </span>
          </div>
          {hasProSubscription && (
            <>
              {(user.subscriptionStatus === 'active' || user.subscriptionStatus === 'cancelled' || isTrialing) && user.subscriptionRenewsAt && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {isTrialing
                      ? t('profile.subscriptionTrialEndsAt')
                      : user.subscriptionStatus === 'active'
                        ? t('profile.subscriptionRenewsAt')
                        : t('profile.subscriptionEndsAt')}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {formatDate(user.subscriptionRenewsAt)}
                  </span>
                </div>
              )}
              {showReactivate && (
                <div className="pt-2">
                  {resumeSuccess && (
                    <p className="text-sm text-green-600 dark:text-green-400 mb-2">
                      {t('profile.reactivateSuccess')}
                    </p>
                  )}
                  {resumeError && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1 mb-2">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {resumeError}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleResumeSubscription}
                    disabled={resumeLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {resumeLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Sparkles className="h-4 w-4 shrink-0" />
                    {t('profile.reactivateSubscription')}
                  </button>
                </div>
              )}
            </>
          )}
          {!hasProSubscription && (
            <div className="pt-2 space-y-2">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('profile.noSubscription')}
              </p>
              <Link
                to={panelPath('upgrade')}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                <Sparkles className="h-4 w-4 shrink-0" />
                {t('nav.upgradeToPro')}
              </Link>
            </div>
          )}
          {cancelSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400">
              {t('profile.cancelSuccess')}
            </p>
          )}
          {cancelError && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {cancelError}
            </p>
          )}
          {canCancelSubscription && (
            <div className="pt-2">
              {!cancelConfirmOpen ? (
                <button
                  type="button"
                  onClick={() => setCancelConfirmOpen(true)}
                  className="text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
                >
                  {t('profile.cancelSubscription')}
                </button>
              ) : (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                    {t('profile.cancelSubscriptionConfirm')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCancelSubscription}
                      disabled={cancelLoading}
                      className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      {cancelLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t('profile.cancelConfirmButton')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCancelConfirmOpen(false);
                        setCancelError(null);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
